import moment from "moment";

const HIDDEN_JSON_KEYS = new Set([
  "id",
  "uid",
  "createdBy",
  "createdDate",
  "created_by",
  "created_date",
  "updatedBy",
  "updatedDate",
  "updated_by",
  "updated_date",
  "deletedBy",
  "deletedOn",
  "deleted_by",
  "deleted_on",
  "isDeleted",
  "is_deleted",
  "document",
  "documentName",
  "fileName",
  "originalFileName",
  "accountId",
  "projectId",
]);

const JSON_COLUMN_FIELDS: Record<
  string,
  { key: string; label: string; sourceKeys: string[] }[]
> = {
  salaryRevisionHistory: [
    {
      key: "salary",
      label: "Salary",
      sourceKeys: ["previousSalary", "salary", "ctc"],
    },
    { key: "effectiveFrom", label: "Effective From", sourceKeys: ["effectiveFrom"] },
  ],
  bonusHistory: [
    {
      key: "bonusAmount",
      label: "Bonus Amount",
      sourceKeys: ["bonusAmount", "bonus", "amount"],
    },
    { key: "issuedDate", label: "Issued Date", sourceKeys: ["issuedDate"] },
  ],
  positionHistory: [
    { key: "effectiveFrom", label: "Effective From", sourceKeys: ["effectiveFrom"] },
    { key: "designation", label: "Designation", sourceKeys: ["designation"] },
    {
      key: "reportingTo",
      label: "Reporting To",
      sourceKeys: ["reportingTo", "reportingToName", "lineManager", "lineManagerName"],
    },
  ],
  primarySkills: [
    { key: "skill", label: "Skill", sourceKeys: ["skill", "name", "skillName"] },
    {
      key: "proficiencyLevel",
      label: "Proficiency",
      sourceKeys: ["proficiencyLevel", "proficiency", "level"],
    },
  ],
  skillDetails: [
    { key: "skill", label: "Skill", sourceKeys: ["skill", "name", "skillName"] },
    {
      key: "proficiencyLevel",
      label: "Proficiency",
      sourceKeys: ["proficiencyLevel", "proficiency", "level"],
    },
  ],
  projectTimeline: [
    { key: "date", label: "Date", sourceKeys: ["date", "effectiveFrom"] },
    { key: "client", label: "Client", sourceKeys: ["accountName", "client"] },
    { key: "project", label: "Project", sourceKeys: ["projectName", "project"] },
  ],
};

export const parseReportJsonValue = (value: any) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed || !["[", "{"].includes(trimmed[0])) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const getConfiguredFieldLabel = (key: string): string | undefined => {
  for (const columnKey of Object.keys(JSON_COLUMN_FIELDS)) {
    const field = JSON_COLUMN_FIELDS[columnKey].find((item) => item.key === key);
    if (field) return field.label;
  }

  return undefined;
};

export const formatReportFieldLabel = (key: string): string =>
  getConfiguredFieldLabel(key) ||
  key
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const isDateLikeKey = (key: string): boolean =>
  /date|from|to|start|end/i.test(key);

const formatPrimitive = (value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const formatFieldValue = (key: string, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  if (isDateLikeKey(key) && moment(value).isValid()) {
    return moment(value).format("DD-MMM-YYYY");
  }
  if (Array.isArray(value)) {
    return value.map(formatPrimitive).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    return formatObject(value);
  }
  return formatPrimitive(value);
};

const getConfiguredSourceKey = (
  value: Record<string, any>,
  sourceKeys: string[]
): string | undefined =>
  sourceKeys.find(
    (key) => value[key] !== undefined && value[key] !== null && value[key] !== ""
  );

const formatConfiguredObject = (
  value: Record<string, any>,
  columnKey: string
): string => {
  const configuredFields = JSON_COLUMN_FIELDS[columnKey];
  if (!configuredFields) return "";

  return configuredFields
    .map((field) => {
      const sourceKey = getConfiguredSourceKey(value, field.sourceKeys);
      if (!sourceKey) return "";

      const formattedValue = formatFieldValue(sourceKey, value[sourceKey]);
      return formattedValue ? `${field.label}: ${formattedValue}` : "";
    })
    .filter(Boolean)
    .join(", ");
};

const formatObject = (value: Record<string, any>, columnKey?: string): string => {
  if (columnKey && JSON_COLUMN_FIELDS[columnKey]) {
    return formatConfiguredObject(value, columnKey);
  }

  return Object.entries(value)
    .filter(
      ([key, itemValue]) =>
        !isHiddenReportJsonKey(key) &&
        itemValue !== undefined &&
        itemValue !== null &&
        itemValue !== ""
    )
    .map(([key, itemValue]) => {
      const formattedValue = formatFieldValue(key, itemValue);
      return formattedValue ? `${formatReportFieldLabel(key)}: ${formattedValue}` : "";
    })
    .filter(Boolean)
    .join(", ");
};

export const formatReportJsonValue = (value: any, columnKey?: string): string => {
  const parsed = parseReportJsonValue(value);

  if (parsed === undefined || parsed === null || parsed === "") return "";

  if (
    (columnKey === "primarySkills" || columnKey === "skillDetails") &&
    parsed &&
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    Array.isArray(parsed.primarySkills)
  ) {
    return formatReportJsonValue(parsed.primarySkills, "primarySkills");
  }

  if (Array.isArray(parsed)) {
    return parsed
      .map((item, index) => {
        if (item && typeof item === "object") {
          const text = formatObject(item, columnKey);
          return text ? `${index + 1}. ${text}` : "";
        }
        return formatPrimitive(item);
      })
      .filter(Boolean)
      .join("\n");
  }

  if (parsed && typeof parsed === "object") {
    return formatObject(parsed, columnKey);
  }

  return formatPrimitive(parsed);
};

export const isHiddenReportJsonKey = (key: string): boolean =>
  HIDDEN_JSON_KEYS.has(key);

export const getReportJsonFieldKeys = (rows: any[], columnKey: string): string[] => {
  const configuredFields = JSON_COLUMN_FIELDS[columnKey];
  if (configuredFields) {
    return configuredFields.map((field) => field.key);
  }

  const keys: string[] = [];

  rows.forEach((row) => {
    const parsed = parseReportJsonValue(row?.[columnKey]);
    const items = Array.isArray(parsed) ? parsed : [parsed];

    items.forEach((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return;

      Object.keys(item).forEach((key) => {
        if (isHiddenReportJsonKey(key) || keys.includes(key)) return;
        keys.push(key);
      });
    });
  });

  return keys;
};

export const formatReportJsonFieldValue = (
  value: any,
  columnKeyOrFieldKey: string,
  maybeFieldKey?: string
): string => {
  const columnKey = maybeFieldKey ? columnKeyOrFieldKey : "";
  const fieldKey = maybeFieldKey || columnKeyOrFieldKey;
  const configuredField = JSON_COLUMN_FIELDS[columnKey]?.find(
    (field) => field.key === fieldKey
  );
  const sourceKeys = configuredField?.sourceKeys || [fieldKey];
  const getFieldValue = (item: any): string => {
    if (!item || typeof item !== "object") return "";
    const sourceKey = sourceKeys.find(
      (key) => item[key] !== undefined && item[key] !== null && item[key] !== ""
    );
    return sourceKey ? formatFieldValue(sourceKey, item[sourceKey]) : "";
  };
  const parsed = parseReportJsonValue(value);

  if (Array.isArray(parsed)) {
    return parsed
      .map((item) => getFieldValue(item))
      .filter(Boolean)
      .join("\n");
  }

  if (parsed && typeof parsed === "object") {
    return getFieldValue(parsed);
  }

  return "";
};
